/**
 * m3 echo Frontend - Module Dialog
 *
 * Form for editing a billing module's parameters.
 * Module creation is done manually (DB/API).
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Alert,
  CircularProgress,
  Box,
  Chip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { BillingModule, BillingModuleUpdate, billingApi } from '../../services/billing';

interface ModuleDialogProps {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  module: BillingModule | null;
}

const typeLabels: Record<string, string> = {
  quota: 'Kontingent',
  model: 'KI-Modell',
  feature: 'Feature',
  custom: 'Custom',
};

const ModuleDialog: React.FC<ModuleDialogProps> = ({ open, onClose, onSaved, module }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [displayName, setDisplayName] = useState('');
  const [description, setDescription] = useState('');
  const [basePrice, setBasePrice] = useState<number>(0);
  const [gracePeriodDays, setGracePeriodDays] = useState<number>(0);
  const [isActive, setIsActive] = useState(true);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !module) return;

    setDisplayName(module.display_name);
    setDescription(module.description || '');
    setBasePrice(Number(module.base_price));
    setGracePeriodDays(module.grace_period_days);
    setIsActive(module.is_active);
    setError(null);
  }, [open, module]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!module) return;
    setError(null);
    setLoading(true);

    try {
      const data: BillingModuleUpdate = {
        display_name: displayName,
        description: description || undefined,
        base_price: basePrice,
        grace_period_days: gracePeriodDays,
        is_active: isActive,
      };
      await billingApi.updateModule(module.id, data);
      onSaved();
    } catch (err: any) {
      setError(err.message || 'Fehler beim Speichern');
    } finally {
      setLoading(false);
    }
  };

  if (!module) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth fullScreen={isMobile}>
      <form onSubmit={handleSubmit}>
        <DialogTitle>Modul bearbeiten</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TextField
                label="Name"
                value={module.name}
                disabled
                helperText="Name kann nicht geändert werden"
                fullWidth
              />
              <Chip
                label={typeLabels[module.module_type] || module.module_type}
                size="small"
                variant="outlined"
                sx={{ flexShrink: 0 }}
              />
            </Box>

            <TextField
              label="Anzeigename"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              required
              fullWidth
            />

            <TextField
              label="Beschreibung"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              multiline
              rows={2}
              fullWidth
            />

            <TextField
              label="Preis EUR/Monat"
              type="number"
              value={basePrice}
              onChange={(e) => setBasePrice(Math.max(0, parseFloat(e.target.value) || 0))}
              inputProps={{ min: 0, step: 0.01 }}
              fullWidth
            />

            <TextField
              label="Kulanzzeit (Tage)"
              type="number"
              value={gracePeriodDays}
              onChange={(e) => setGracePeriodDays(Math.max(0, parseInt(e.target.value) || 0))}
              inputProps={{ min: 0 }}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={loading}>
            Abbrechen
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading}
            startIcon={loading && <CircularProgress size={20} />}
          >
            Speichern
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default ModuleDialog;
